import React, { useMemo } from 'react';

import {
  ReferenceArrayField,
  Datagrid,
  ArrayField,
  SingleFieldList,
  WrapperField,
  useRecordContext,
  RaRecord,
  RecordContextProvider,
  useRedirect,
  usePermissions,
} from 'react-admin';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from '@mui/material';

import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';

import ReferenceEntityField from '../../../customFields/ReferenceEntityField';
import StatusField from '../../../customFields/StatusField';
import TeamChip from '../../../customFields/ColoredChipField';

import BulkUpdatePings from '../bulkActions/BulkUpdatePings';
import UpdatePingStatus from '../../ping/components/UpdatePingStatus';
import Pagination from '../../layout/Pagination';
import LinkField from '../../layout/LinkField';

import { boldDataGridStyle } from '../../../constants/style/datagridStyles';
import ColoredScoreField from '../../../customFields/ColoredScoreField';
import EntityDisplayNameField from '../../../customFields/EntityDisplayNameField';
import hasAccess from '../../../utilities/hasAccess';
import actions, { Actions } from '../../../constants/actions';
import PermissionMissing from '../../utils/PermissionMissing';
import PingDescription from '../../ping/components/PingDescription';
import { ICase } from '../../../utilities/schemas/case';

const CasePingList = () => {
  const redirect = useRedirect();

  const record = useRecordContext<ICase>();
  const { permissions } = usePermissions<Actions[]>();

  const pings = useMemo(() => record?.pings.map((ping: RaRecord) => ping.id), [record?.pings]);
  if (!record) return null;

  return (
    <Card variant="outlined">
      <CardHeader
        title="Pings"
        action={(
          hasAccess(permissions, actions.PING_CREATE) && (
            <Box>
              <Button onClick={() => redirect('add-ping')} startIcon={<EmojiObjectsIcon />}>
                Create ping
              </Button>
            </Box>
          )
        )}
      />
      <Divider />
      {hasAccess(permissions, actions.PING_GET) ? (
        <RecordContextProvider value={{ pings }}>
          <ReferenceArrayField
            perPage={10}
            pagination={<Pagination disableEmptyText />}
            reference="pings"
            source="pings"
          >
            <Datagrid
              sx={boldDataGridStyle}
              bulkActionButtons={(
                <BulkUpdatePings />
              )}
              resource="pings"
            >
              <WrapperField label="ID">
                <LinkField sortable={false} source="id" label="ID" />
              </WrapperField>
              <ArrayField sortable={false} source="entities" label="Entities">
                <SingleFieldList linkType={false}>
                  <ReferenceEntityField source="">
                    <EntityDisplayNameField chip />
                  </ReferenceEntityField>
                </SingleFieldList>
              </ArrayField>
              <ReferenceArrayField sortable={false} source="tags" reference="tags" label="Tags" emptyText="-">
                <SingleFieldList placeholder="-">
                  <TeamChip />
                </SingleFieldList>
              </ReferenceArrayField>
              <PingDescription caseId={record.id} sortable={false} source="description" />
              <WrapperField label="Status">
                <Box display="flex">
                  <StatusField sortable={false} source="status" />
                  { hasAccess(permissions, actions.PING_UPDATE_STATUS)
                  && (<UpdatePingStatus isIcon />) }
                </Box>
              </WrapperField>
              <ColoredScoreField source="score" />
            </Datagrid>
          </ReferenceArrayField>
        </RecordContextProvider>
      ) : (
        <CardContent>
          <Box display="flex" alignItems="center" width="100%" flexDirection="column">
            <PermissionMissing />
          </Box>
        </CardContent>
      )}
    </Card>
  );
};

export default CasePingList;
